import React, { useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";

const defaultFormData = {
  icon: "",
  title: "",
  description: "",
  benefits: [],
  stats: {
    metric: "",
    label: "",
  },
  solutions: [],
  isActive: true,
};

const IndustryFormModal = ({
  mode,
  industry,
  products,
  onClose,
  onSave,
  apiBaseUrl,
}) => {
  const [formData, setFormData] = useState(industry || defaultFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState("");

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleStatsChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      stats: {
        ...prev.stats,
        [field]: value,
      },
    }));
  };

  const handleAddBenefit = () => {
    setFormData((prev) => ({
      ...prev,
      benefits: [...prev.benefits, ""],
    }));
  };

  const handleRemoveBenefit = (index) => {
    setFormData((prev) => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index),
    }));
  };

  const handleBenefitChange = (index, value) => {
    setFormData((prev) => {
      const newBenefits = [...prev.benefits];
      newBenefits[index] = value;
      return { ...prev, benefits: newBenefits };
    });
  };

  const handleAddSolution = () => {
    if (!selectedProductId) {
      toast.error("Please select a product first");
      return;
    }

    // Check if product is already added
    const isAlreadyAdded = formData.solutions.some(
      (sol) => sol.productId === selectedProductId
    );

    if (isAlreadyAdded) {
       toast.error("This product is already added to solutions");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      solutions: [
        ...prev.solutions,
        { productId: selectedProductId },
      ],
    }));
    // console.log("solid" , selectedProductId)
    setSelectedProductId("");
  };

  const handleRemoveSolution = (index) => {
    setFormData((prev) => ({
      ...prev,
      solutions: prev.solutions.filter((_, i) => i !== index),
    }));
  };

  const getProductName = (productId) => {
    const product = products.find((p) => p._id === productId);
    return product?.heading || "Unknown Product";
  };

  const submitForm = async () => {
    try {
      setError(null);
      setLoading(true);

      // Validate required fields
      if (!formData.title.trim()) {
        setError("Title is required");
        setLoading(false);
        return;
      }

      if (!formData.description.trim()) {
        setError("Description is required");
        setLoading(false);
        return;
      }

      if (!formData.stats.metric || !formData.stats.label) {
        setError("Metric and Label are required");
        setLoading(false);
        return;
      }

      let url, method;

      if (mode === "add") {
        url = `${apiBaseUrl}/create`;
        method = "POST";
      } else if (mode === "edit") {
        url = `${apiBaseUrl}/${industry._id}`;
        method = "PUT";
      }

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Failed to save industry");

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl my-8">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === "add" ? "Add New Industry" : "Edit Industry"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-3 border-b">
            {error}
          </div>
        )}

        {/* Modal Content */}
        <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Basic Information
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Icon (Emoji or Unicode)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="icon"
                    value={formData.icon}
                    onChange={handleFormChange}
                    maxLength="10"
                    className="w-20 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base text-center"
                    placeholder="🏭"
                  />
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Industry Title *"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="isActive"
                  value={formData.isActive}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isActive: e.target.value === "true",
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </select>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Industry description"
                />
              </div>
            </div>

            {/* Stats */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Key Metrics
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Metric Value *
                  </label>
                  <input
                    type="text"
                    value={formData.stats.metric}
                    onChange={(e) => handleStatsChange("metric", e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 45%"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Metric Label *
                  </label>
                  <input
                    type="text"
                    value={formData.stats.label}
                    onChange={(e) => handleStatsChange("label", e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Cost Reduction"
                  />
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Benefits
              </h3>
              {formData.benefits.map((benefit, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={benefit}
                    onChange={(e) => handleBenefitChange(idx, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter benefit"
                  />
                  <button
                    onClick={() => handleRemoveBenefit(idx)}
                    className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                onClick={handleAddBenefit}
                className="mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg"
              >
                + Add Benefit
              </button>
            </div>

            {/* Solutions */}
            <div className="pb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Related Solutions (Products)
              </h3>

              {/* Add New Solution */}
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Product to Add
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Choose a product...</option>
                    {products.map((product) => (
                      <option key={product._id} value={product._id}>
                        {product.heading}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAddSolution}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Current Solutions */}
              {formData.solutions.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-700 text-sm">
                    Added Solutions:
                  </h4>
                  {formData.solutions.map((solution, idx) => (
                    <div
                      key={idx}
                      className="flex justify-between items-center p-3 bg-gray-100 rounded-lg"
                    >
                      <span className="font-medium text-gray-900">
                        {getProductName(solution.productId)}
                      </span>
                      <button
                        onClick={() => handleRemoveSolution(idx)}
                        className="px-3 py-1 text-sm bg-red-100 hover:bg-red-200 text-red-600 rounded"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {formData.solutions.length === 0 && (
                <div className="text-center py-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-gray-500 text-sm">
                    No solutions added yet
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end gap-4 p-6 border-t border-gray-200 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={submitForm}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {loading
              ? "Saving..."
              : mode === "add"
                ? "Create Industry"
                : "Update Industry"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IndustryFormModal;